import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { doc, getDoc, setDoc, addDoc, deleteDoc, collection, query, where, orderBy, onSnapshot, writeBatch, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import { CC, CC_TEXT, CL, CATEGORIES, VALID_CATEGORIES, VALID_BOOKING_STATUSES } from "./constants";
import Img from "./Img";
import ActivityCard from "./ActivityCard";
import ActivityEditPanel from "./ActivityEditPanel";
import DayEditPanel from "./DayEditPanel";
import Toast, { useToast } from "./Toast";
import { searchPhoto } from "../utils/pexels";
import { COLOR, FONT } from "../utils/theme";
import { Icon } from "../utils/icons";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import useWindowWidth from "../utils/useWindowWidth";
import { getCostRange } from "../utils/costHelpers";
import { authenticatedFetch } from "../utils/apiClient";
import { takeSnapshot } from "../utils/snapshots";
import { isDuplicate } from "../utils/dedup";
import { formatCost } from "../utils/formatCost";
import { getSetting } from "../utils/settings";

const API_URL = process.env.REACT_APP_PROXY_URL || 'http://localhost:3001';

const labelStyle = {
  fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono,
  letterSpacing: "0.1em", textTransform: "uppercase",
  marginBottom: 5, display: "block",
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: COLOR.white, border: `1px solid ${COLOR.borderWarm}`,
  borderRadius: 8, padding: "10px 12px",
  color: COLOR.inkDark, fontSize: 14,
  fontFamily: FONT.serif,
  outline: "none", transition: "border-color 0.15s",
};

// Normalize a Firestore activity doc to the shape components expect
function normalizeActivity(data) {
  return {
    ...data,
    time: data.timeBlock || data.time || "Morning", // migration used timeBlock, edits use time
    img:  data.img      || data.photo || null,        // img is canonical; photo is legacy migration field
  };
}

const sanitizeActivity = (act) => ({
  ...act,
  category: VALID_CATEGORIES.includes(act.category)
    ? act.category : 'leisure',
  bookingStatus: VALID_BOOKING_STATUSES.includes(act.bookingStatus)
    ? act.bookingStatus : 'not_needed',
});

export default function DayView({ day, selDay, days, goDay, onDayUpdate, onDeleteDay, tripData, onRegisterFlush, tripStatus, onCompleteDay, activityTitlesByDay = {} }) {
  const { isDesktop } = useWindowWidth();
  const [showAlt, setShowAlt] = useState(false);
  const [localActivities, setLocalActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [editingAct, setEditingAct] = useState(null);
  const [editingDay, setEditingDay] = useState(false);
  const [localDay, setLocalDay] = useState(day);
  const [heroImg, setHeroImg] = useState(day.heroImg || null);
  const [heroLightboxOpen, setHeroLightboxOpen] = useState(false);

  // Derive display date from tripData.startDate + day.order (not stored on day doc)
  const dayDisplayDate = useMemo(() => {
    const startStr = tripData?.startDate || tripData?.brief?.departureDate;
    if (startStr && day.order) {
      try {
        const base = new Date(startStr + "T00:00:00Z");
        if (!isNaN(base)) {
          const d = new Date(base);
          d.setUTCDate(d.getUTCDate() + (day.order - 1));
          return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
        }
      } catch { /* fall through */ }
    }
    return day.date || `Day ${day.order || day.day}`;
  }, [tripData, day.order, day.day, day.date]);

  useEffect(() => {
    if (!localDay.city) return;
    const src = localDay.heroImg || "";
    if (src && !src.includes("picsum")) { setHeroImg(src); return; }
    const q = `${localDay.city} ${localDay.country} travel`;
    searchPhoto(q, localDay.heroImg).then(url => { if (url) setHeroImg(url); });
  }, [localDay.city, localDay.country, localDay.heroImg]);

  const [toast, showToast] = useToast();

  const handleDayUpdate = useCallback((dayId, changes) => {
    setLocalDay(prev => ({ ...prev, ...changes }));
    if (changes.heroImg) setHeroImg(changes.heroImg);
    onDayUpdate?.(dayId, changes);
  }, [onDayUpdate]);

  // Real-time listener for primary activities on this day
  useEffect(() => {
    setActivitiesLoading(true);
    const q = query(
      collection(db, "activities"),
      where("ownerUid", "==", auth.currentUser.uid),
      where("dayId", "==", day.id),
      where("planType", "==", "primary"),
      orderBy("order")
    );
    const unsubscribe = onSnapshot(q,
      (snap) => {
        setLocalActivities(snap.docs.map(d => normalizeActivity({ ...d.data(), id: d.id })));
        setActivitiesLoading(false);
      },
      (e) => {
        console.error("[DayView] Failed to fetch activities:", e);
        showToast("Failed to load activities — " + (e.message?.includes("index") ? "Firestore index missing. Check console for setup URL." : e.message), "error");
        setActivitiesLoading(false);
      }
    );
    return unsubscribe;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day.id]);

  const handleActivitySave = useCallback(() => {
    setEditingAct(null);
  }, []);

  const handleActivityUpdate = useCallback((activityId, changes) => {
    setLocalActivities(prev => prev.map(a => a.id === activityId ? { ...a, ...changes } : a));
  }, []);

  const MAX_ACTIVITIES = 10;

  const handleAddActivity = useCallback(async () => {
    if (localActivities.length >= MAX_ACTIVITIES) return;
    const newOrder = localActivities.length > 0
      ? Math.max(...localActivities.map(a => a.order || 0)) + 1
      : 1;
    const newAct = {
      tripId: day.tripId || "",
      ownerUid: auth.currentUser.uid,
      dayId: day.id,
      title: "New Activity",
      category: "leisure",
      timeBlock: "morning",
      time: "Morning",
      order: newOrder,
      planType: "primary",
      detail: "",
      duration: "",
      cost: "",
      locked: false,
      version: 1,
    };
    try {
      const docRef = await addDoc(collection(db, "activities"), newAct);
      const created = { ...newAct, id: docRef.id, img: null, _isNew: true };
      setLocalActivities(prev => [...prev, { ...created, _isNew: undefined }]);
      setEditingAct({ act: created, mode: "edit" });
    } catch (e) {
      console.error("[DayView] Failed to add activity:", e);
      showToast("⚠️ Failed to add activity", "error");
    }
  }, [localActivities, day.id, day.tripId, showToast]);

  const handleDeleteActivity = useCallback(async (actId) => {
    try {
      await deleteDoc(doc(db, "activities", actId));
      setLocalActivities(prev => prev.filter(a => a.id !== actId));
      setEditingAct(null);
      showToast("Activity deleted", "success");
    } catch (e) {
      console.error("[DayView] Failed to delete activity:", e);
      showToast("⚠️ Failed to delete activity", "error");
    }
  }, [showToast]);

  const handleDragEnd = useCallback(async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(localActivities);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    const withOrder = reordered.map((a, i) => ({ ...a, order: i + 1 }));
    setLocalActivities(withOrder);
    try {
      const batch = writeBatch(db);
      withOrder.forEach(a => batch.update(doc(db, "activities", a.id), { order: a.order }));
      await batch.commit();
    } catch (e) {
      console.error("[DayView] Failed to save order:", e);
      showToast("⚠️ Failed to save order", "error");
    }
  }, [localActivities, showToast]);

  // ── Day-level notes ────────────────────────────────────────────────────────
  const [notes, setNotes] = useState("");
  const [saveStatus, setSaveStatus] = useState("idle"); // "idle" | "saving" | "saved" | "error"
  const debounceRef = useRef(null);
  const savedTimerRef = useRef(null);
  const textareaRef = useRef(null);

  const dayDocId = day.id;

  useEffect(() => {
    async function loadNotes() {
      try {
        const snap = await getDoc(doc(db, "days", dayDocId));
        if (snap.exists()) setNotes(snap.data()?.notes || "");
      } catch (e) {}
    }
    loadNotes();
  }, [dayDocId]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [notes]);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(savedTimerRef.current);
    };
  }, []);

  const handleNotesChange = useCallback((e) => {
    const value = e.target.value;
    setNotes(value);
    setSaveStatus("saving");
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await setDoc(doc(db, "days", dayDocId), { notes: value }, { merge: true });
        setSaveStatus("saved");
        clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (e) {
        setSaveStatus("error");
        clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 3000);
      }
    }, 800);
  }, [dayDocId]);

  const saveNotesNow = useCallback(async () => {
    clearTimeout(debounceRef.current);
    try {
      await setDoc(doc(db, "days", dayDocId), { notes }, { merge: true });
    } catch (err) {
      console.error("[DayView] notes flush failed:", err);
    }
  }, [dayDocId, notes]);

  useEffect(() => {
    if (onRegisterFlush) onRegisterFlush(saveNotesNow);
    return () => { if (onRegisterFlush) onRegisterFlush(null); };
  }, [onRegisterFlush, saveNotesNow]);

  useEffect(() => {
    if (!heroLightboxOpen) return;
    const handler = e => { if (e.key === "Escape") setHeroLightboxOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [heroLightboxOpen]);

  // ── AI day rewrite ─────────────────────────────────────────────────────────
  // "hidden" | "prompt" | "loading" | "preview" | "error"
  const [aiMode, setAiMode] = useState("hidden");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiPreview, setAiPreview] = useState(null);
  const [aiError, setAiError] = useState("");
  const [aiClosing, setAiClosing] = useState(false);
  const [aiDayMeta, setAiDayMeta] = useState(null); // { dayTitle, dayTip } from AI

  const dismissAI = useCallback(() => {
    setAiClosing(true);
    setTimeout(() => {
      setAiMode("hidden");
      setAiPrompt("");
      setAiPreview(null);
      setAiError("");
      setAiClosing(false);
      setAiDayMeta(null);
    }, 260);
  }, []);

  const handleAIRewriteDay = async () => {
    if (day.locked) {
      showToast("This day is approved — unapprove it to rewrite.", "error");
      dismissAI();
      return;
    }
    if (!aiPrompt.trim() || aiMode === "loading") return;
    setAiMode("loading");
    setAiError("");
    // sanitized — control chars stripped, capped at 500
    const safePrompt = aiPrompt
      .trim()
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001F\u007F]/g, ' ')
      .slice(0, 500);

    const lockedActivities = localActivities.filter(a => a != null && a.locked);
    const otherTitles = Object.entries(activityTitlesByDay)
      .filter(([dayId]) => dayId !== day.id)
      .flatMap(([, titles]) => titles);
    const cities = [...new Set(days.map(d => d.city))].join(", ");

    const userMessage = [
      "Current day:",
      JSON.stringify({ city: day.city, date: dayDisplayDate, theme: day.theme, tip: day.tip, country: day.country }, null, 2),
      "",
      "Current activities for this day:",
      JSON.stringify(localActivities.map(a => ({ id: a.id, title: a.title, timeBlock: a.timeBlock, duration: a.duration, category: a.category, cost: a.cost, locked: a.locked })), null, 2),
      "",
      `User request: ${safePrompt}`,
      "",
      (() => {
        const tripTitle = tripData?.title || "";
        const tripTravelers = tripData?.brief?.travelers?.join(", ") || tripData?.participants?.join(", ") || "";
        const tripStyle = Array.isArray(tripData?.brief?.style)
          ? tripData.brief.style.join(', ')
          : (tripData?.brief?.style || "");
        const tripBudget = tripData?.brief?.budget || "";
        let line = `Trip: ${tripTitle} · ${tripTravelers} · Style: ${tripStyle} · Budget: ${tripBudget} · Cities: ${cities} · Day hours: 8am–10pm · Max activities: 8`;
        if (tripData?.brief) {
          line += `\nMust-sees: ${tripData.brief.mustSees || 'none'} · Avoid: ${tripData.brief.avoid || 'none'} (Items prefixed "Declined suggestion:" are specific rejected activities — avoid only that exact activity, not the broader category) · Departure: ${tripData.brief.departureCity || 'unspecified'}`;
        }
        const routingSequence = days
          .map(d => d.transitMode ? `${d.city} (${d.transitMode})` : d.city)
          .join(' → ');
        if (routingSequence) {
          line += `\nRouting: ${routingSequence}`;
        }
        return line;
      })(),
      "",
      lockedActivities.length > 0 ? [
        "LOCKED activities (return exactly unchanged):",
        lockedActivities.map(a => `- ${a.id}: ${a.title}`).join("\n"),
      ].join("\n") : null,
      "",
      "Other activities on this trip (avoid duplicates):",
      otherTitles.map(t => `- ${t}`).join("\n"),
      "",
      "Return between 3 and 8 activity objects depending on how long each activity takes. Consider the total duration of all activities — do not schedule more than 14 hours in a single day. A day with long activities (3+ hours each) should have fewer total activities than a day of short ones.",
      "Preserve on each object (do not modify): id, tripId, dayId, order, planType, notes, confirmed, locked, aiGenerated, version, previousVersion, photo.",
      "Reuse existing activity IDs where possible. Return locked activities completely unchanged.",
    ].filter(Boolean).join("\n");

    try {
      let dayRewriteSystem = "You are a travel planning assistant. Return ONLY valid JSON with this exact shape — no markdown, no backticks, no explanation:\n{\"dayTitle\": \"Short evocative theme for the day (3-6 words)\", \"dayTip\": \"One practical travel tip for the day (1-2 sentences)\", \"activities\": [...]}\nUse \"category\" (transport, accommodation, dining, culture, nature, leisure, shopping, nightlife) on every activity object. Return a numeric \"cost\" field (estimated per-person cost in USD). For bookingUrl, provide only the homepage domain (e.g. https://restaurant-kontrast.no), never a deep link. For bookingNote, include 2-3 word search terms the user can use to find the specific listing. Always include a location object with name, address, lat, lng, and placeId using real coordinates. Be concise in detail fields — 1-2 sentences maximum per activity.";
      if (day.budgetOverride) {
        dayRewriteSystem += `\nBudget guidance for this day: ${day.budgetOverride}. Adjust activity cost levels accordingly — 'budget' means prioritize free/cheap options, 'moderate' means mid-range, 'splurge' means premium experiences.`;
      }
      if (day.pace) {
        dayRewriteSystem += `\nPace for this day: ${day.pace}. 'relaxed' means fewer activities with more downtime, 'moderate' means a balanced day, 'packed' means maximize activities and minimize gaps.`;
      }
      const res = await authenticatedFetch(`${API_URL}/api/claude`, {
        method: "POST",
        body: JSON.stringify({
          system: dayRewriteSystem,
          messages: [{ role: "user", content: userMessage }],
          max_tokens: 6000,
          language: getSetting('wanderologie_settings.language') || 'English',
          model: getSetting('wanderologie_settings.aiModel'),
        }),
      });

      if (res.status === 429) {
        const errData = await res.json();
        showToast(errData.message, "error");
        return;
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error?.message || `API error ${res.status}`);
      }

      const data = await res.json();
      const raw = (data.content?.[0]?.text || "").trim()
        .replace(/^```json?\n?/, "").replace(/```$/, "").trim();

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (syntaxErr) {
        throw new Error("⚠️ AI response was cut off — try again or request fewer activities");
      }
      // Support both { activities, dayTitle, dayTip } shape and legacy bare array
      const activitiesArray = Array.isArray(parsed) ? parsed : parsed?.activities;
      if (!Array.isArray(activitiesArray)) throw new Error("Expected a JSON array");
      const safeActivities = activitiesArray
        .filter(a => a && a.title)
        .map((a, idx) => ({
          ...a,
          id: localActivities[idx]?.id || `${day.id}-act-${idx + 1}`,
          order: idx + 1,      // Rule 14: always from array index, never from AI
          planType: localActivities[idx]?.planType || "primary", // structural — never from AI
          dayId: day.id,       // always override — AI must never change this
          tripId: day.tripId,  // always override — AI must never change this
        }));
      if (safeActivities.length === 0) throw new Error("AI returned invalid data — please try again");

      // Dedup: drop activities whose titles near-match other days' activities
      const dedupTitles = [...otherTitles];
      const dedupedActivities = safeActivities.filter(act => {
        if (act.locked) return true; // never drop locked activities
        if (isDuplicate(act.title, dedupTitles)) {
          console.log(`[DEDUP] Day rewrite dropped "${act.title}"`);
          return false;
        }
        dedupTitles.push(act.title);
        return true;
      });

      const dayMeta = !Array.isArray(parsed) ? {
        dayTitle: parsed.dayTitle || null,
        dayTip: parsed.dayTip || null,
      } : null;
      if (dayMeta) setAiDayMeta(dayMeta);

      // Show preview immediately — persist to Firestore in background
      setAiPreview(dedupedActivities);
      setAiMode("preview");
      writeActivitiesToFirestore(dedupedActivities, dayMeta).catch(err => {
        console.error("Background write failed:", err);
        showToast("⚠️ Rewrite shown but save failed — try again");
      });
    } catch (e) {
      if (e.message.includes("declined") || e.message.includes("refusal")) {
        showToast("✦ Couldn't generate — try rephrasing your request", "error");
        setAiMode("prompt");
      } else if (e.message.includes("truncated") || e.message.includes("too many activities") || e.message.includes("cut off")) {
        showToast("⚠️ Response too long — try asking for fewer activities", "error");
        setAiMode("prompt");
      } else {
        setAiError(e.message);
        setAiMode("error");
      }
    }
  };

  // Write AI-rewritten activities to Firestore — called on response land
  const writeActivitiesToFirestore = async (activities, dayMeta) => {
    const sanitized = activities.map(sanitizeActivity);

    // Batch write: delete old, write new with sanitized fields
    const batch = writeBatch(db);
    localActivities.forEach(a => batch.delete(doc(db, "activities", a.id)));
    sanitized.forEach((activity, idx) => {
      const existing = localActivities.find(a => a.id === activity.id);
      const docRef = doc(db, "activities", activity.id);
      batch.set(docRef, {
        ...activity,
        dayId: day.id,
        tripId: day.tripId || "",
        ownerUid: auth.currentUser.uid,
        planType: "primary",
        costRange: getCostRange(activity.cost),
        order: idx + 1,
        previousVersion: existing
          ? (() => { const { previousVersion: _p, ...clean } = existing; return clean; })()
          : null,
        version: ((existing?.version) || 1) + 1,
      });
    });

    // 3. Update day doc with theme/tip + lastRewriteAt
    const dayUpdates = { lastRewriteAt: serverTimestamp() };
    if (dayMeta?.dayTitle) dayUpdates.theme = dayMeta.dayTitle;
    if (dayMeta?.dayTip) dayUpdates.tip = dayMeta.dayTip;

    // Fire-and-forget snapshot before committing writes
    takeSnapshot(db, day.tripId, `AI Rewrite: Day ${day.order || day.day} ${day.city}`, days, localActivities).catch(console.error);

    // Batch write and day update in parallel
    await Promise.all([
      batch.commit(),
      setDoc(doc(db, "days", dayDocId), dayUpdates, { merge: true }),
    ]);
    if (dayMeta?.dayTitle || dayMeta?.dayTip) {
      handleDayUpdate(day.id, dayUpdates);
    }

    // 4. Update local state only after Firestore succeeds
    setLocalActivities(sanitized);
    showToast(`Day updated — ${sanitized.length} activities saved`, "success");
  };

  // Undo AI rewrite — restore previousVersion from each activity
  const handleUndoRewrite = async () => {
    try {
      const batch = writeBatch(db);
      localActivities.forEach(act => {
        if (act.previousVersion) {
          batch.set(doc(db, "activities", act.id), { ...act.previousVersion, ownerUid: auth.currentUser.uid });
        }
      });
      await batch.commit();
      const restored = localActivities
        .filter(act => act.previousVersion)
        .map(act => act.previousVersion);
      setLocalActivities(restored);
      await setDoc(doc(db, "days", dayDocId), { lastRewriteAt: null }, { merge: true });
      dismissAI();
      showToast("Rewrite undone", "success");
    } catch (err) {
      console.error("[DayView] undo failed:", err);
      showToast("Undo failed — try again", "error");
    }
  };

  return (
    <>
      {editingAct && (
        <ActivityEditPanel
          act={editingAct.act}
          mode={editingAct.mode}
          onSave={handleActivitySave}
          onActivityUpdate={handleActivityUpdate}
          onClose={() => setEditingAct(null)}
          onDeleteActivity={handleDeleteActivity}
          otherActivityTitles={localActivities.filter(a => a.id !== editingAct.act.id).map(a => a.title)}
          tripCities={[...new Set(days.map(d => d.city))]}
          tripData={tripData}
          day={day}
        />
      )}

      {editingDay && (
        <DayEditPanel
          day={localDay}
          tripId={day.tripId || ""}
          onClose={() => setEditingDay(false)}
          onSave={(changes) => {
            setLocalDay(prev => ({ ...prev, ...changes }));
            onDayUpdate?.(day.id, changes);
            setEditingDay(false);
          }}
        />
      )}

      <Toast message={toast.message} type={toast.type} visible={toast.visible} />

      {/* AI Day Rewrite Overlay — hidden when day has no activities */}
      {aiMode !== "hidden" && (
        <div
          onClick={dismissAI}
          style={{
            position: "fixed", inset: 0, zIndex: 300,
            display: "flex", alignItems: "flex-end",
            background: aiClosing ? `${COLOR.inkDark}00` : `${COLOR.inkDark}80`,
            transition: "background 0.26s",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: COLOR.cream, borderRadius: "24px 24px 0 0",
              width: "100%", maxWidth: 680,
              maxHeight: "92vh", overflowY: "auto", WebkitOverflowScrolling: "touch",
              paddingBottom: "max(16px, env(safe-area-inset-bottom))",
              animation: `${aiClosing ? "slideDown" : "slideUp"} 0.26s cubic-bezier(0.32,0.72,0,1) forwards`,
            }}
          >
            {/* Drag handle */}
            <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: COLOR.borderWarm }} />
            </div>

            {/* Header */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "10px 20px 14px", borderBottom: `1px solid ${COLOR.borderWarm}`,
            }}>
              <div>
                <div style={{ fontSize: 10, color: COLOR.mauve, fontFamily: FONT.mono, letterSpacing: "0.12em", marginBottom: 3 }}>
                  <Icon name="rewrite" size={12} color={COLOR.mauve} style={{ marginRight: 4, verticalAlign: "middle" }} /> {aiMode === "preview" ? "DAY REWRITTEN — SAVED" : "REWRITE DAY WITH AI"}
                </div>
                <div style={{ fontSize: 16, color: COLOR.inkDark, fontFamily: FONT.serif, lineHeight: 1.2 }}>
                  Day {localDay.day} — {localDay.theme}
                </div>
              </div>
              <button
                onClick={dismissAI}
                style={{ background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 10, color: COLOR.inkMid, width: 44, height: 44, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
              >
                <Icon name="close" size={16} color={COLOR.inkMid} />
              </button>
            </div>

            {aiMode === "preview" ? (
              <DayPreviewPanel
                preview={aiPreview}
                dayMeta={aiDayMeta}
                onDismiss={dismissAI}
                onUndo={handleUndoRewrite}
              />
            ) : (
              <div style={{ padding: "20px 20px 16px" }}>
                <label style={labelStyle}><Icon name="rewrite" size={12} color={COLOR.mauve} style={{ marginRight: 4, verticalAlign: "middle" }} /> WHAT WOULD YOU LIKE TO CHANGE?</label>
                <textarea
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  onFocus={e => e.target.style.borderColor = COLOR.mauve}
                  onBlur={e => e.target.style.borderColor = COLOR.borderWarm}
                  placeholder="e.g. Make this a slower, more food-focused day"
                  rows={4}
                  disabled={aiMode === "loading"}
                  style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6, marginBottom: 10, opacity: aiMode === "loading" ? 0.6 : 1 }}
                />

                {aiError && (
                  <div style={{ fontSize: 12, color: COLOR.mauveDark, fontFamily: FONT.mono, marginBottom: 10, padding: "8px 12px", background: COLOR.altCardGradient, borderRadius: 8, border: `1px solid ${COLOR.mauve}33` }}>
                    ⚠ {aiError}
                  </div>
                )}

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={handleAIRewriteDay}
                    disabled={aiMode === "loading" || !aiPrompt.trim()}
                    style={{
                      flex: 2, border: "none", borderRadius: 10,
                      background: aiMode === "loading" ? COLOR.borderWarm : COLOR.mauve,
                      color: aiMode === "loading" ? COLOR.inkMid : COLOR.white,
                      padding: "11px", fontSize: 13, fontFamily: FONT.mono,
                      cursor: aiMode === "loading" || !aiPrompt.trim() ? "default" : "pointer",
                      minHeight: 44,
                    }}
                  >
                    <Icon name="rewrite" size={14} color={aiMode === "loading" ? COLOR.inkMid : COLOR.white} style={{ marginRight: 4, verticalAlign: "middle" }} /> {aiMode === "loading" ? "Thinking…" : "Rewrite Day"}
                  </button>
                  <button
                    onClick={dismissAI}
                    style={{
                      flex: 1, background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`,
                      borderRadius: 10, color: COLOR.inkMid, padding: "11px",
                      cursor: "pointer", fontSize: 13, fontFamily: FONT.mono, minHeight: 44,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hero lightbox */}
      {heroLightboxOpen && (
        <div
          onClick={() => setHeroLightboxOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 600, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", animation: "fadeIn 0.18s ease" }}
        >
          <button
            onClick={() => setHeroLightboxOpen(false)}
            style={{ position: "absolute", top: 14, right: 14, background: `${COLOR.inkDark}80`, border: `1px solid ${COLOR.white}33`, borderRadius: 10, color: COLOR.white, width: 44, height: 44, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <Icon name="close" size={16} color={COLOR.white} />
          </button>
          <img
            src={heroImg}
            alt={localDay.theme}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8 }}
          />
        </div>
      )}

      {/* Hero */}
      <div
        data-testid="day-hero"
        style={{ position: "relative", height: 280, flexShrink: 0, cursor: heroImg ? "zoom-in" : "default" }}
        onClick={heroImg ? () => setHeroLightboxOpen(true) : undefined}
      >
        <Img
          src={heroImg}
          alt={localDay.theme}
          style={{ width: "100%", height: "100%" }}
          objectPosition={localDay.heroCropParams ? `${localDay.heroCropParams.x}% ${localDay.heroCropParams.y}%` : undefined}
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.55) 100%)" }} />

        {/* Progress dots */}
        <div data-testid="hero-dots" style={{ position: "absolute", top: 12, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 6, filter: "drop-shadow(0px 1px 3px rgba(0,0,0,0.4))" }}>
          {days.map((_, i) => (
            <div
              key={i}
              onClick={e => { e.stopPropagation(); goDay(i); }}
              style={{
                padding: 8, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <div style={{
                width: i === selDay ? 20 : 6, height: 6, borderRadius: 3,
                background: i === selDay ? COLOR.white : i < selDay ? `${COLOR.white}66` : `${COLOR.white}33`,
                transition: "all 0.25s",
              }} />
            </div>
          ))}
        </div>

        {/* Edit day button */}
        <button
          onClick={e => { e.stopPropagation(); setEditingDay(true); }}
          style={{
            position: "absolute", top: 10, right: 12,
            background: `${COLOR.inkDark}73`, border: `1px solid ${COLOR.white}40`,
            borderRadius: 8, color: COLOR.white, width: 44, height: 44,
            cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center",
            WebkitTapHighlightColor: "transparent",
          }}
          title="Edit day"
        >
          <Icon name="edit" size={15} color={COLOR.white} />
        </button>

        {/* Day info overlay */}
        <div style={{ position: "absolute", bottom: 14, left: 18, right: 18 }}>
          <div data-testid="day-theme" style={{ fontSize: 28, fontWeight: 500, color: COLOR.white, marginBottom: 3, textShadow: `0 1px 8px ${COLOR.inkDark}80`, fontFamily: FONT.display, letterSpacing: '-0.02em', ...(!isDesktop ? { overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" } : {}) }}>
            {localDay.theme}
          </div>
          <div data-testid="day-subtitle" style={{ fontSize: 12, color: `${COLOR.white}cc`, textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>
            Day {localDay.day} · {localDay.country} · {dayDisplayDate}
          </div>
          {localDay.climate && (
            <div data-testid="day-climate" style={{ fontSize: 12, color: `${COLOR.white}aa`, fontFamily: FONT.serif, marginTop: 3, fontStyle: "italic", textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>
              {localDay.climate}
            </div>
          )}
        </div>
      </div>

      {/* Sticky tip bar with alt plan toggle + AI rewrite button */}
      <div data-testid="day-tip" style={{
        position: "sticky", top: 0, zIndex: 10,
        background: COLOR.blushLight, backdropFilter: "blur(8px)",
        borderBottom: `1px solid ${COLOR.borderWarm}`, borderLeft: `3px solid ${COLOR.mauveLight}`, flexShrink: 0,
        display: "flex", alignItems: "center", gap: 10,
        padding: "8px 12px 8px 16px",
      }}>
        <span style={{ flexShrink: 0, display: "flex", alignItems: "center" }}><Icon name="tip" size={14} color={COLOR.mauve} /></span>
        <span style={{ fontSize: 12, color: COLOR.inkMid, lineHeight: 1.55, flex: 1, minWidth: 0, ...(localDay.tip ? {} : { fontStyle: "italic" }) }}>
          {localDay.tip ? (
            !isDesktop ? (
              <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{localDay.tip}</span>
            ) : localDay.tip
          ) : (tripData?.brief?.destinations?.length > 1
              ? "Your must-see highlights are ready to explore. The full day-by-day itinerary is being curated now."
              : "We're curating your day-by-day plan. Activities will appear here shortly.")}
        </span>
        <button
          data-testid="day-action-button"
          onClick={(!activitiesLoading && localActivities.length === 0 && tripStatus !== 'creating') ? onCompleteDay : (tripStatus === 'creating' && localActivities.length === 0) ? undefined : () => setAiMode("prompt")}
          disabled={tripStatus === 'creating' && localActivities.length === 0}
          style={{
            flexShrink: 0, background: "transparent",
            border: `1px solid ${COLOR.mauve}44`,
            borderRadius: 8, color: COLOR.mauve,
            padding: "8px 16px", fontSize: 13, fontFamily: FONT.mono,
            letterSpacing: "0.04em", cursor: (tripStatus === 'creating' && localActivities.length === 0) ? "default" : "pointer",
            transition: "all 0.15s", WebkitTapHighlightColor: "transparent",
            whiteSpace: "nowrap", minHeight: 44,
            display: "flex", alignItems: "center",
            opacity: (tripStatus === 'creating' && localActivities.length === 0) ? 0.5 : 1,
          }}
        >
          <Icon name="rewrite" size={14} color={COLOR.mauve} style={{ marginRight: 4, verticalAlign: "middle" }} /> {(!activitiesLoading && localActivities.length === 0) ? (tripStatus === 'creating' ? "Generating…" : "Complete Day") : "Rewrite Day"}
        </button>
        {day.altPlan && (
          <button
            onClick={() => setShowAlt(s => !s)}
            style={{
              flexShrink: 0, background: showAlt ? `${COLOR.mauve}18` : "transparent",
              border: `1px solid ${showAlt ? `${COLOR.mauve}44` : COLOR.borderWarm}`,
              borderRadius: 8, color: showAlt ? COLOR.mauve : COLOR.inkMid,
              padding: "8px 16px", fontSize: 13, fontFamily: FONT.mono,
              letterSpacing: "0.04em", cursor: "pointer",
              transition: "all 0.15s", WebkitTapHighlightColor: "transparent",
              whiteSpace: "nowrap", minHeight: 44,
              display: "flex", alignItems: "center",
            }}
          >
            {showAlt ? "▲ Alt" : "◆ Alt plan"}
          </button>
        )}
      </div>

      {/* Undo rewrite banner */}
      {!!day.lastRewriteAt && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 16px', background: COLOR.warmSurface,
          borderBottom: `1px solid ${COLOR.borderWarm}`,
          fontSize: 13, color: COLOR.inkMid, fontStyle: 'italic',
        }}>
          <span>AI rewrote this day</span>
          <button
            onClick={handleUndoRewrite}
            style={{
              background: 'none', border: 'none', color: COLOR.mauve,
              cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
              fontStyle: 'italic', padding: '4px 8px',
            }}
          >
            Undo
          </button>
        </div>
      )}

      {/* Day notes */}
      <div style={{ padding: "12px 12px 0" }}>
        <div style={{ background: COLOR.white, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 12, padding: "12px 14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono, letterSpacing: "0.08em", display: "flex", alignItems: "center", gap: 4 }}><Icon name="notes" size={13} color={COLOR.inkMid} /> NOTES — Day {day.day}, {dayDisplayDate}</span>
            {saveStatus === "saved" && (
              <span style={{ fontSize: 11, color: COLOR.savedGreen, fontFamily: FONT.mono, opacity: 0.8 }}>
                Saved <Icon name="confirm" size={11} color={COLOR.savedGreen} />
              </span>
            )}
            {saveStatus === "error" && (
              <span style={{ fontSize: 11, color: COLOR.mauveDark, fontFamily: FONT.mono, opacity: 0.9 }}>
                <Icon name="warning" size={14} color={COLOR.mauveDark} style={{ marginRight: 3, verticalAlign: "middle" }} /> Failed to save
              </span>
            )}
          </div>
          <textarea
            ref={textareaRef}
            value={notes}
            onChange={handleNotesChange}
            placeholder={`Notes for Day ${day.day} — ${day.city}…`}
            rows={3}
            style={{
              width: "100%", boxSizing: "border-box",
              background: COLOR.cream, border: `1px solid ${COLOR.borderWarm}`,
              borderRadius: 8, padding: "10px 12px",
              color: COLOR.inkDark, fontSize: 13,
              fontFamily: FONT.serif,
              lineHeight: 1.6, resize: "none", overflow: "hidden",
              outline: "none", transition: "border-color 0.15s",
            }}
            onFocus={e => e.target.style.borderColor = COLOR.mauve}
            onBlur={e => e.target.style.borderColor = COLOR.borderWarm}
          />
        </div>
      </div>

      {/* Primary activity cards — always rendered if they exist */}
      <div style={{ padding: "10px 12px 0" }}>
        {activitiesLoading ? (
          <div style={{ padding: "24px 0", textAlign: "center", fontFamily: FONT.mono, fontSize: 12, color: COLOR.inkMid, opacity: 0.6, letterSpacing: "0.08em" }}>
            Loading activities…
          </div>
        ) : (!activitiesLoading && localActivities.length === 0 && tripStatus === 'creating') ? (
          <div data-testid="generation-skeleton" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Show highlight titles if available for this day */}
            {activityTitlesByDay[day.id] && activityTitlesByDay[day.id].length > 0 && (
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>
                  Planned highlights
                </div>
                {activityTitlesByDay[day.id].map((title, i) => (
                  <div key={i} style={{ fontSize: 13, color: COLOR.inkDark, marginBottom: 4 }}>
                    {'\u2022'} {title}
                  </div>
                ))}
              </div>
            )}
            {/* Skeleton placeholder cards */}
            <style>{`
              @keyframes dvSkeletonPulse {
                0%, 100% { opacity: 0.4; }
                50% { opacity: 1; }
              }
              @keyframes dvSpinner {
                to { transform: rotate(360deg); }
              }
            `}</style>
            {[1, 2].map(i => (
              <div key={i} style={{
                background: COLOR.warmSurface,
                border: `1px solid ${COLOR.borderWarm}`,
                borderRadius: 12,
                overflow: "hidden",
              }}>
                <div style={{
                  height: 8,
                  background: `linear-gradient(90deg, ${COLOR.borderWarm}, ${COLOR.mauveLight}40, ${COLOR.borderWarm})`,
                  animation: "dvSkeletonPulse 1.5s ease-in-out infinite",
                  animationDelay: `${(i - 1) * 0.3}s`,
                }} />
                <div style={{ padding: 12, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{
                    display: "inline-block",
                    width: 12, height: 12,
                    border: `2px solid ${COLOR.mauve}`,
                    borderTopColor: "transparent",
                    borderRadius: "50%",
                    animation: "dvSpinner 0.8s linear infinite",
                  }} />
                  <span style={{ fontSize: 13, color: COLOR.inkMid, fontStyle: "italic" }}>
                    Activities generating...
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="activities">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  {localActivities.map((act, index) => (
                    <Draggable key={act.id} draggableId={act.id} index={index}>
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          data-testid={`activity-card-${act.id}`}
                          style={{
                            position: "relative",
                            display: "flex",
                            alignItems: "stretch",
                            background: dragSnapshot.isDragging ? `${COLOR.mauve}0f` : "transparent",
                            borderRadius: 12,
                            ...dragProvided.draggableProps.style,
                          }}
                        >
                          {/* Drag handle — desktop only */}
                          <div
                            {...dragProvided.dragHandleProps}
                            style={{
                              display: "none",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 24,
                              flexShrink: 0,
                              cursor: "grab",
                              color: COLOR.dragHandle,
                            }}
                            className="drag-handle"
                          >
                            <Icon name="drag" size={16} color={COLOR.dragHandle} />
                          </div>
                          <style>{`.drag-handle { display: none; } @media (min-width: 768px) { .drag-handle { display: flex !important; } }`}</style>

                          <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
                            <ActivityCard
                              act={act}
                              dayCity={localDay.city}
                              dayCountry={localDay.country}
                              onClick={() => setEditingAct({ act, mode: "view" })}
                              onEdit={() => setEditingAct({ act, mode: "edit" })}
                              onActivityUpdate={handleActivityUpdate}
                            />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {/* Add Activity button — hide during generation with no activities */}
        {!(tripStatus === 'creating' && localActivities.length === 0) && <div style={{ marginTop: 10 }}>
          {localActivities.length >= MAX_ACTIVITIES ? (
            <div style={{ textAlign: "center", fontSize: 12, fontFamily: FONT.mono, color: COLOR.inkMid, opacity: 0.7, padding: "10px 0" }}>
              Maximum 10 activities reached
            </div>
          ) : (
            <button
              data-testid="add-activity-button"
              onClick={handleAddActivity}
              style={{
                width: "100%", background: "transparent",
                border: `1px solid ${COLOR.mauve}66`, borderRadius: 12,
                color: COLOR.mauve, padding: "12px", fontSize: 13,
                fontFamily: FONT.mono, cursor: "pointer",
                letterSpacing: "0.04em", minHeight: 44,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              + Add Activity
            </button>
          )}
        </div>}
      </div>

      {/* Alternative plan drawer */}
      {showAlt && day.altPlan && (
        <div style={{ margin: "14px 12px 0", borderRadius: 14, overflow: "hidden", border: `1px solid ${COLOR.mauve}22` }}>
          <div style={{
            background: `linear-gradient(135deg, ${COLOR.altCardGradient}, ${COLOR.white})`,
            padding: "12px 14px",
            borderBottom: `1px solid ${COLOR.mauve}18`,
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <div style={{ fontSize: 10, color: COLOR.mauve, fontFamily: FONT.mono, letterSpacing: "0.1em", marginBottom: 2 }}>◆ ALTERNATIVE PLAN</div>
              <div style={{ fontSize: 14, color: COLOR.inkDark, fontFamily: FONT.serif }}>{day.altPlan.theme}</div>
            </div>
            <button
              onClick={() => setShowAlt(false)}
              style={{ background: "transparent", border: "none", color: COLOR.inkMid, cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "4px 6px" }}
            >
              <Icon name="close" size={16} color={COLOR.inkMid} />
            </button>
          </div>
          <div style={{ background: COLOR.cardAltBody, padding: "10px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
            {day.altPlan.activities.map(act => (
              <ActivityCard key={act.id} act={act} isAlt dayCity={localDay.city} dayCountry={localDay.country} />
            ))}
          </div>
        </div>
      )}

    </>
  );
}

// ── Day AI Preview Panel ──────────────────────────────────────────────────────

function DayPreviewPanel({ preview, dayMeta, onDismiss, onUndo }) {
  return (
    <div style={{ padding: "18px 20px 8px" }}>
      {(dayMeta?.dayTitle || dayMeta?.dayTip) && (
        <div style={{ background: COLOR.altCardGradient, border: `1px solid ${COLOR.mauve}33`, borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
          {dayMeta.dayTitle && (
            <div style={{ fontSize: 15, color: COLOR.inkDark, fontFamily: FONT.serif, marginBottom: dayMeta.dayTip ? 6 : 0 }}>
              <Icon name="rewrite" size={14} color={COLOR.mauve} style={{ marginRight: 4, verticalAlign: "middle" }} /> {dayMeta.dayTitle}
            </div>
          )}
          {dayMeta.dayTip && (
            <div style={{ fontSize: 12, color: COLOR.inkMid, lineHeight: 1.55 }}>
              <Icon name="tip" size={13} color={COLOR.mauve} style={{ marginRight: 4, verticalAlign: "middle" }} /> {dayMeta.dayTip}
            </div>
          )}
        </div>
      )}
      <div style={{ fontSize: 11, color: COLOR.inkMid, fontFamily: FONT.mono, letterSpacing: "0.1em", marginBottom: 14 }}>
        SAVED ACTIVITIES — {preview.length} total
      </div>

      {preview.map((act, i) => (
        <div
          key={act.id || i}
          style={{
            background: act.locked ? COLOR.warmSurface : COLOR.white,
            border: `1px solid ${act.locked ? COLOR.borderWarm : `${COLOR.mauve}33`}`,
            borderRadius: 12, padding: "12px 14px", marginBottom: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            {CATEGORIES[act.category]?.iconName && (
              <Icon name={CATEGORIES[act.category].iconName} size={20} color={COLOR.inkMid} />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, color: COLOR.inkDark, fontFamily: FONT.serif, lineHeight: 1.3 }}>
                {act.title}
                {act.locked && (
                  <span style={{ marginLeft: 6, fontSize: 10, color: COLOR.lockedGreen, fontFamily: FONT.mono, background: `${COLOR.lockedGreen}15`, borderRadius: 4, padding: "1px 5px" }}>
                    APPROVED ✓
                  </span>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <span style={{
              fontSize: 10, fontFamily: FONT.mono, borderRadius: 5, padding: "2px 7px",
              background: COLOR.buttonSecondary, color: COLOR.inkMid,
            }}>
              {act.time}
            </span>
            {act.category && CATEGORIES[act.category] ? (
              <span style={{
                fontSize: 10, fontFamily: FONT.mono, borderRadius: 5, padding: "2px 7px",
                background: COLOR.categoryBadgeBg, color: COLOR.categoryBadgeText,
              }}>
                <Icon name={CATEGORIES[act.category].iconName} size={11} color={COLOR.categoryBadgeText} style={{ marginRight: 3, verticalAlign: "middle" }} /> {CATEGORIES[act.category].label}
              </span>
            ) : act.costType ? (
              <span style={{
                fontSize: 10, fontFamily: FONT.mono, borderRadius: 5, padding: "2px 7px",
                background: CC[act.costType] || COLOR.borderWarm,
                color: CC_TEXT[act.costType] || COLOR.inkMid,
              }}>
                {CL[act.costType] || act.costType}
              </span>
            ) : null}
            {(() => {
              const cr = act.costRange || getCostRange(act.cost);
              return (
                <span style={{
                  fontSize: 10, fontFamily: FONT.mono, borderRadius: 5, padding: "2px 7px",
                  background: COLOR.warmSurface, color: COLOR.inkMid, fontWeight: 700,
                }}>
                  {cr === "free" ? "Free" : cr}
                </span>
              );
            })()}
            {act.cost != null && act.cost !== 0 && (
              <span style={{ fontSize: 11, color: COLOR.inkMid, fontFamily: FONT.mono }}>
                {formatCost(act.cost)}
              </span>
            )}
          </div>
        </div>
      ))}

      <div style={{ fontSize: 10, fontFamily: FONT.mono, color: COLOR.inkMid, opacity: 0.75, textAlign: "center", padding: "8px 16px 0" }}>
        <Icon name="aiSparkle" size={10} color={COLOR.inkMid} style={{ marginRight: 4, verticalAlign: "middle" }} /> AI-suggested · Verify prices, hours and availability before booking.
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
        <button
          onClick={onDismiss}
          style={{
            flex: 2, border: "none", borderRadius: 12, color: COLOR.white,
            padding: "14px", fontSize: 15, fontFamily: FONT.mono,
            minHeight: 52, cursor: "pointer",
            background: COLOR.mauve,
          }}
        >
          Looks good <Icon name="confirm" size={14} color={COLOR.white} />
        </button>
        <button
          onClick={onUndo}
          style={{
            flex: 1, background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`,
            borderRadius: 12, color: COLOR.inkMid, padding: "14px",
            cursor: "pointer", fontSize: 14, fontFamily: FONT.mono, minHeight: 52,
          }}
        >
          Undo
        </button>
      </div>
    </div>
  );
}
